import Vue from 'vue'

/** mutation type identifiers */
export const SET_MENU = 'setMenu'
export const SET_LOADING = 'setLoading'
export const SET_ERROR = 'setError'
export const TOGGLE_EXPANDED = 'toggleExpanded'
export const FETCH_MENU = 'fetchMenu'

/** Menu item IDs that should be expanded by default (user can collapse them) */
const DEFAULT_EXPANDED_IDS = ['orders_dispatch']

function isExpanded(id, expandedIds, collapsedIds) {
  if (collapsedIds.includes(id)) return false
  return DEFAULT_EXPANDED_IDS.includes(id) || expandedIds.includes(id)
}

/**
 * Prepare menu items: add isOpen from expandedIds/collapsedIds (persists across route changes)
 */
function applyExpandedState(menu, expandedIds, collapsedIds = []) {
  if (!menu || !menu.length) return menu
  return menu.map((item) => {
    const result = { ...item }
    if (result.children && result.children.length) {
      result.isOpen = isExpanded(result.id, expandedIds, collapsedIds)
      result.children = applyExpandedState(result.children, expandedIds, collapsedIds)
    }
    return result
  })
}

/**
 * Prepare menu items for sidebar: add isOpen for expandable items (initial load)
 */
function prepareMenu(menu) {
  return menu.map((item) => {
    const prepared = { ...item }
    if (prepared.children && prepared.children.length) {
      prepared.isOpen = DEFAULT_EXPANDED_IDS.includes(prepared.id)
      prepared.children = prepareMenu(prepared.children)
    }
    return prepared
  })
}

export default {
  namespaced: true,

  state: {
    menu: [],
    loading: false,
    error: null,
    expandedIds: [],
    collapsedIds: [],
  },

  mutations: {
    [SET_MENU](state, menu) {
      state.menu = prepareMenu(menu || [])
      state.error = null
    },
    collapseAllExcept(state, id) {
      state.expandedIds = state.expandedIds.filter((x) => x === id)
      DEFAULT_EXPANDED_IDS.forEach((defaultId) => {
        if (defaultId !== id && !state.collapsedIds.includes(defaultId)) {
          state.collapsedIds.push(defaultId)
        }
      })
    },
    [TOGGLE_EXPANDED](state, id) {
      const isCollapsed = state.collapsedIds.includes(id)
      const isDefaultExpanded = DEFAULT_EXPANDED_IDS.includes(id)
      const isInExpanded = state.expandedIds.includes(id)

      if (isCollapsed) {
        state.collapsedIds = state.collapsedIds.filter((x) => x !== id)
      } else if (isDefaultExpanded && !isInExpanded) {
        state.collapsedIds.push(id)
      } else {
        const idx = state.expandedIds.indexOf(id)
        if (idx >= 0) {
          state.expandedIds.splice(idx, 1)
        } else {
          state.expandedIds.push(id)
        }
      }
    },
    [SET_LOADING](state, loading) {
      state.loading = loading
    },
    [SET_ERROR](state, error) {
      state.error = error
      state.menu = []
    },
  },

  actions: {
    [FETCH_MENU]({ commit }, payload) {
      const type = payload?.type ?? (typeof window !== 'undefined' ? window.__MENU_TYPE__ : null) ?? process.env.VUE_APP_MENU_TYPE ?? 'console'
      commit(SET_LOADING, true)
      return Vue.$http
        .get('admin-menu', { params: { type } })
        .then((response) => {
          commit(SET_MENU, response.data)
          return response.data
        })
        .catch((err) => {
          commit(SET_ERROR, err)
          throw err
        })
        .finally(() => {
          commit(SET_LOADING, false)
        })
    },
  },

  getters: {
    menu: (state) => applyExpandedState(state.menu, state.expandedIds || [], state.collapsedIds || []),
    applyExpanded: (state) => (menu) => applyExpandedState(menu || [], state.expandedIds || [], state.collapsedIds || []),
    hasMenu: (state) => state.menu && state.menu.length > 0,
    isLoading: (state) => state.loading,
    error: (state) => state.error,
  },
}
